/**
 * #5176 review 1 - THE GOVERNED NAME, UNIT AND WORDING OF EVERY M&A SYNERGY FIGURE THE
 * MANAGER CAN PLAN AGAINST.
 *
 * The manager resolves a definition for every `answerAnchors.metricKey` in the catalog
 * and projects its unit into the planning vocabulary. A key with no registry row projects
 * a UNITLESS metric, and the plan validator then refuses any objective that states a
 * result unit for it - which is exactly how five governed, executable M&A intents came to
 * fail at planning with `manager_plan_metric_unit_mismatch` while their approved evidence
 * sat in the tenant. Declaring the metric HERE is what gives the planner a unit to agree
 * with; it creates no value and admits no evidence.
 *
 * CERTAINTY CLASSES NEVER BLEND. Every register below carries `never_blend` and names its
 * siblings in `neverSumWith`, so the module's standing law - underwritten, committed and
 * banked dollars are never summed into one total - is enforced by the registry rather than
 * restated in prose. No entry declares `module_composite_contribution_v1`: none of these
 * figures rolls into the objective value bridge, and claiming otherwise would invite the
 * very sum the law forbids.
 *
 * The UNITS here are the same units the module declares beside the capability that emits
 * them. That agreement is pinned by a conformance test in the module itself, so the two
 * can never drift apart silently.
 */
import type { GovernedMeasureIdentity } from './governed-measure-identity';

const MA_SYNERGY_REGISTER_SIBLINGS = Object.freeze([
  'underwritten_c2_modelled_upside_usd',
  'committed_watched_c1_usd',
  'realized_observed_actuals_usd',
] as const);

const MA_SYNERGY_EVIDENCE_CLASSES = Object.freeze([
  'ma_synergy_realization_governed_read_v1',
] as const);

export interface MaSynergyTrackingMetricRegistrySeedInput {
  readonly metricKey: string;
  readonly label: string;
  readonly registryLayer: 'core';
  readonly appliesTo: readonly [];
  readonly aggregationRules: readonly [];
  readonly blendPolicy: Readonly<{ kind: 'never_blend' }>;
  readonly benchmarkable: false;
  readonly sourceOfTruthClass: 'derived';
  readonly ownerModuleId: 'ma-synergy-tracking';
  readonly sourceBindingKeys: readonly string[];
  readonly requiredEvidenceClasses: typeof MA_SYNERGY_EVIDENCE_CLASSES;
  readonly neverSumWith: readonly (typeof MA_SYNERGY_REGISTER_SIBLINGS)[number][];
  readonly unit: string;
  readonly temporalType: 'flow' | 'ratio' | 'count';
  readonly direction: 'higher_is_better' | 'lower_is_better' | 'context';
  readonly definition: string;
  readonly reconciliationRule: string;
  readonly source: string;
  // Structural fix (#5734 measure-identity sweep): this seed-input shape previously
  // had no way to carry measureIdentity through to the metricEntry() builder it
  // maps over in metric-registry.ts, so all 14 M&A synergy-tracking rows were
  // structurally incapable of ever declaring one.
  readonly measureIdentity?: GovernedMeasureIdentity;
}

export const MA_SYNERGY_TRACKING_METRIC_REGISTRY_SEED_INPUTS: readonly MaSynergyTrackingMetricRegistrySeedInput[] =
  Object.freeze([
    {
      metricKey: 'underwritten_c2_modelled_upside_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: underwritten_c2_modelled_upside_usd',
      definition:
        'Base-case annual run-rate value of the deal synergy levers underwritten as modelled upside, summed across those levers.',
      reconciliationRule:
        'Sum the base-case band only from approved modelled-upside underwriting artifacts for this deal; never add committed or observed dollars to it.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:underwritten_c2_modelled_upside_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['underwritten_c2_modelled_upside_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: ['committed_watched_c1_usd', 'realized_observed_actuals_usd'],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'committed_watched_c1_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: committed_watched_c1_usd',
      definition:
        'Value committed under an approved governed decision and watched against an approved realization curve, summed across those commitments.',
      reconciliationRule:
        'Sum the committed base of dollar-denominated committed levers only; a lever watched in another unit and a lever with an unreadable base contribute nothing and are named as gaps.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:committed_watched_c1_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['committed_watched_c1_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: ['underwritten_c2_modelled_upside_usd', 'realized_observed_actuals_usd'],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'realized_observed_actuals_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: realized_observed_actuals_usd',
      definition:
        'Value banked from reported observed actuals across every governed commitment on the deal, landed observed levers included.',
      reconciliationRule:
        'Sum reported dollar-denominated actual movement on governed commitments only; a commitment with no reported actual contributes nothing rather than zero-scoring the deal.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:realized_observed_actuals_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['realized_observed_actuals_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: ['underwritten_c2_modelled_upside_usd', 'committed_watched_c1_usd'],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'realized_against_committed_c1_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: realized_against_committed_c1_usd',
      definition:
        'The slice of banked value that belongs to the committed and watched levers - the numerator the committed-base share is measured with.',
      reconciliationRule:
        'Sum reported dollar-denominated actual movement on committed levers only; landed observed levers outside the committed base are excluded so both sides of the share describe one population.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:realized_against_committed_c1_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['realized_against_committed_c1_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'unrealized_against_committed_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: unrealized_against_committed_usd',
      definition:
        'Committed and watched value less the value banked against it - what the commitment still owes at the current run rate.',
      reconciliationRule:
        'Subtract the banked committed slice from the committed base on the same governed commitments in the same read; never mix a modelled band into either side.',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'lower_is_better',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:unrealized_against_committed_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['unrealized_against_committed_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
    },
    {
      metricKey: 'at_risk_projected_miss_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: at_risk_projected_miss_usd',
      definition:
        'Projected shortfall against the approved realization curve, summed across the governed commitments whose projected-miss evidence resolved.',
      reconciliationRule:
        'Sum projected miss only on governed commitments carrying a resolved projected-miss row; a commitment without one is named as a gap rather than scored at zero risk.',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'lower_is_better',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:at_risk_projected_miss_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['at_risk_projected_miss_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
    },
    {
      metricKey: 'evidence_gap_count',
      measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: evidence_gap_count',
      definition:
        'Count of deal synergy levers carrying a named evidence gap in this governed read - one card per lever, whatever combination of reasons it collected.',
      reconciliationRule:
        'Count synergy levers, never reasons: a lever holding several gap reasons is one gap, and a lever with none is not counted.',
      unit: 'count',
      temporalType: 'count',
      direction: 'lower_is_better',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:evidence_gap_count',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['evidence_gap_count'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
    },
    {
      metricKey: 'realization_against_committed_pct',
      measureIdentity: { measuredDimension: 'other', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'other' },
      label: 'M&A Synergy Tracking: realization_against_committed_pct',
      definition:
        'Value banked against the committed base as a share of that base, at the current governed run rate.',
      reconciliationRule:
        'Divide the banked committed slice by the committed base on the same governed commitments in the same read; the share is withheld outright when either side cannot account for every governed commitment.',
      unit: 'percent',
      temporalType: 'ratio',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:realization_against_committed_pct',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['realization_against_committed_pct'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'deal_standalone_target_operating_income_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_standalone_target_operating_income_usd',
      definition:
        'The acquisition target operating income on a standalone basis, before any combination effect.',
      reconciliationRule:
        'Read the standalone operating income from the approved target pack for the stated fiscal period; never model it from the acquirer figures.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_standalone_target_operating_income_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_standalone_target_operating_income_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'deal_gross_synergies_conservative_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_gross_synergies_conservative_usd',
      definition:
        'Annual run-rate synergy value underwritten for this deal at the approved conservative band, summed across the underwritten levers.',
      reconciliationRule:
        'Sum the approved conservative band only from approved deal-scoped underwriting artifacts; never mix bands and never add an observed actual to a band.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_gross_synergies_conservative_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_gross_synergies_conservative_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'deal_gross_synergies_base_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_gross_synergies_base_usd',
      definition:
        'Annual run-rate synergy value underwritten for this deal at the approved base band, summed across the underwritten levers.',
      reconciliationRule:
        'Sum the approved base band only from approved deal-scoped underwriting artifacts; never mix bands and never add an observed actual to a band.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_gross_synergies_base_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_gross_synergies_base_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'deal_gross_synergies_stretch_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_gross_synergies_stretch_usd',
      definition:
        'Annual run-rate synergy value underwritten for this deal at the approved stretch band, summed across the underwritten levers.',
      reconciliationRule:
        'Sum the approved stretch band only from approved deal-scoped underwriting artifacts; never mix bands and never add an observed actual to a band.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_gross_synergies_stretch_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_gross_synergies_stretch_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
    {
      metricKey: 'deal_cost_to_achieve_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_cost_to_achieve_usd',
      definition:
        'One-time cost underwritten to capture the deal synergies, summed across the underwritten levers.',
      reconciliationRule:
        'Sum the approved one-time cost only from approved deal-scoped underwriting artifacts; never net it against a run-rate band inside a single figure.',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'lower_is_better',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_cost_to_achieve_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_cost_to_achieve_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
    },
    {
      metricKey: 'deal_combined_operating_income_bridge_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'M&A Synergy Tracking: deal_combined_operating_income_bridge_usd',
      definition:
        'Target standalone operating income plus gross synergies less the cost to achieve them — a combined operating-income bridge, not enterprise value: it carries no valuation multiple and is never presented as one.',
      reconciliationRule:
        'Add the standalone operating income and the stated synergy band, then subtract the one-time cost to achieve, all on the same deal, with each input leg naming its own evidence family and period rather than one borrowed timestamp for all three.',
      unit: 'USD',
      temporalType: 'flow',
      source:
        'apps/api/src/decision-modules/ma-synergy-tracking/ma-synergy-tracking.read-metrics.ts#metricKey:deal_combined_operating_income_bridge_usd',
      registryLayer: 'core',
      appliesTo: [],
      aggregationRules: [],
      blendPolicy: { kind: 'never_blend' },
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'ma-synergy-tracking',
      sourceBindingKeys: ['deal_combined_operating_income_bridge_usd'],
      requiredEvidenceClasses: ['ma_synergy_realization_governed_read_v1'],
      neverSumWith: [
        'underwritten_c2_modelled_upside_usd',
        'committed_watched_c1_usd',
        'realized_observed_actuals_usd',
      ],
      direction: 'higher_is_better',
    },
  ]);
